import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Tag, Button, Space, Table, Alert, Spin, Typography,
  DatePicker, Modal, message, Collapse
} from 'antd';
import { ArrowLeftOutlined, ReloadOutlined, PrinterOutlined, FileTextOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ErpMasterShell } from '../../../../components/common/erp-grid';
import useAuth from '../../../../hooks/useAuth';
import { workOrdersApi } from './workOrdersApi';
import WorkOrderLineGrid from './WorkOrderLineGrid';
import WorkOrderStageMatrix from './WorkOrderStageMatrix';
import WorkOrderPiecesGrid from './WorkOrderPiecesGrid';
import WorkOrderStatsPanel from './WorkOrderStatsPanel';
import { loadFabricationLookupsSafe } from '../../masters/fabrications/fabricationApi';
import { dguConsumableIssueApi } from '../../inventory/dguConsumables/dguConsumableIssueApi';
import { kindLabel, kindColor } from '../../masters/dguConsumables/dguConsumableHelpers';
import {
  canViewWorkOrders, canPrintStickers, canReleaseWorkOrder
} from './workOrderHelpers';
import '../production.css';

// ════════════════════════════════════════════════════════════════════
// Stage WO-1A — Work Order detail (Production).
//
// Header + snapshot lines (with derived route) + the full piece register
// with pre-assigned barcodes. The piece grid is searchable / exportable so
// the shop can pull barcodes for the (next) thermal QR sticker run.
// ════════════════════════════════════════════════════════════════════

function backendError(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.error && err.response.data.error.message)
    || (err && err.message) || fallback || 'Something went wrong.';
}
function fmtDateTime(v) {
  if (!v) return '—';
  const d = dayjs(v);
  return d.isValid() ? d.format('DD MMM YYYY, HH:mm') : '—';
}

const { Text } = Typography;

const NF3 = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 3 });

// ── DGU consumables issued against this work order (read-only, lazy) ──
// Reuses the Phase-3 issue list (filtered by workOrderId) + get (for the lines,
// fetched on row-expand). Renders nothing when the WO has no consumable issues
// or the viewer lacks stock-view — so non-DGU orders stay clean.
function WorkOrderDguIssues({ workOrderId }) {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linesById, setLinesById] = useState({});

  useEffect(() => {
    if (!workOrderId) return undefined;
    let alive = true;
    setLoading(true);
    dguConsumableIssueApi.list({ workOrderId, pageSize: 100 })
      .then((res) => { if (alive) setIssues((res && res.rows) || []); })
      .catch(() => { if (alive) setIssues([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [workOrderId]);

  const loadLines = useCallback((issueId) => {
    if (linesById[issueId]) return;
    dguConsumableIssueApi.get(issueId)
      .then((doc) => setLinesById((p) => ({ ...p, [issueId]: (doc && doc.lines) || [] })))
      .catch(() => setLinesById((p) => ({ ...p, [issueId]: [] })));
  }, [linesById]);

  if (!loading && !issues.length) return null;

  const columns = [
    { title: 'Issue no', dataIndex: 'issueNo', width: 190, render: (v) => <code className="erp-grid__code">{v}</code> },
    { title: 'Status', dataIndex: 'status', width: 110,
      render: (v) => <Tag color={v === 'REVERSED' ? 'default' : 'green'} style={{ marginInlineEnd: 0 }}>{v}</Tag> },
    { title: 'Lines', dataIndex: 'linesCount', width: 70, align: 'center' },
    { title: 'Date', dataIndex: 'createdAt', width: 150, render: (v) => fmtDateTime(v) }
  ];

  const expandedRowRender = (rec) => {
    const rows = linesById[rec.id];
    if (!rows) return <div style={{ padding: 8, color: '#64748b' }}>Loading…</div>;
    if (!rows.length) return <div style={{ padding: 8, color: '#64748b' }}>No lines.</div>;
    return (
      <Table size="small" rowKey="id" pagination={false} dataSource={rows}
        columns={[
          { title: 'Consumable', dataIndex: 'itemName', ellipsis: true },
          { title: 'Kind', dataIndex: 'consumableKind', width: 130, render: (v) => <Tag color={kindColor(v)} style={{ marginInlineEnd: 0 }}>{kindLabel(v)}</Tag> },
          { title: 'Qty', dataIndex: 'qty', width: 110, align: 'right', render: (v) => <span className="tabular-nums">{NF3.format(Number(v) || 0)}</span> },
          { title: 'Unit', dataIndex: 'stockUom', width: 70 }
        ]} />
    );
  };

  return (
    <>
      <div className="erp-production-section-title">
        DGU consumables issued <Text type="secondary" style={{ fontWeight: 400, fontSize: 11.5 }}>({issues.length} issue{issues.length === 1 ? '' : 's'})</Text>
      </div>
      <Table
        className="erp-accounts-lines" size="small" rowKey="id" loading={loading}
        columns={columns} dataSource={issues} pagination={false}
        expandable={{ expandedRowRender, onExpand: (open, rec) => { if (open) loadLines(rec.id); } }}
        locale={{ emptyText: 'No consumables issued' }}
      />
    </>
  );
}

export default function WorkOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me } = useAuth();
  const mayView = canViewWorkOrders(me && me.permissions);
  const mayPrint = canPrintStickers(me && me.permissions);
  const mayRelease = canReleaseWorkOrder(me && me.permissions);

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // Fabrication Master lookups drive the read-only FABRICATION columns' labels
  // (abbreviations) in the lines grid — loaded once, tolerant of failure.
  const [fabLookups, setFabLookups] = useState([]);

  // F1 — edit the expected delivery date (commitment) on a released WO.
  const [dlvOpen, setDlvOpen] = useState(false);
  const [dlvDate, setDlvDate] = useState(null);
  const [dlvSaving, setDlvSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await workOrdersApi.get(id);
      setDetail(data);
    } catch (err) {
      setDetail(null);
      setLoadError(backendError(err, 'Could not load this work order.'));
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let alive = true;
    loadFabricationLookupsSafe()
      .then((rows) => { if (alive) setFabLookups(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (alive) setFabLookups([]); });
    return () => { alive = false; };
  }, []);

  const openDlv = useCallback((current) => {
    setDlvDate(current ? dayjs(current) : null);
    setDlvOpen(true);
  }, []);
  const saveDlv = useCallback(async () => {
    if (!dlvDate) { message.error('Pick a delivery date.'); return; }
    setDlvSaving(true);
    try {
      await workOrdersApi.setDeliveryDate(id, dlvDate.format('YYYY-MM-DD'));
      message.success('Delivery date updated.');
      setDlvOpen(false);
      load();
    } catch (err) { message.error(backendError(err, 'Could not update the delivery date.')); }
    finally { setDlvSaving(false); }
  }, [dlvDate, id, load]);

  const wo = detail && detail.workOrder;
  const lines = (detail && detail.lines) || [];
  const pieces = (detail && detail.pieces) || [];

  // Overall PIECE completion for the Production-stages board (ordered pieces,
  // excluding remake clones; done = pieces in the COMPLETED state). Derived from
  // the pieces already loaded — no extra request.
  const orderedTotal = useMemo(() => pieces.filter((p) => !p.scrappedFromId).length, [pieces]);
  const completedPieces = useMemo(() => pieces.filter((p) => p.state === 'COMPLETED').length, [pieces]);
  const stageColumns = (detail && detail.stageColumns) || [];
  const stageProgress = (detail && detail.stageProgress) || {};

  return (
    <ErpMasterShell>
      <div className="erp-production-page">
        <div className="flex items-center justify-between mb-3">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/erp/production/work-orders')}>Work Orders</Button>
          <Space size="small">
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
            {wo && mayView ? (
              <Button icon={<FileTextOutlined />}
                onClick={() => navigate(`/erp/production/work-orders/${id}/work-order-copy`)}>
                Print Work Order
              </Button>
            ) : null}
            {wo && mayPrint ? (
              <Button type="primary" icon={<PrinterOutlined />}
                onClick={() => navigate(`/erp/production/work-orders/${id}/stickers`)}>
                Print Stickers
              </Button>
            ) : null}
          </Space>
        </div>

        {loadError ? (
          <Alert type="error" showIcon message={loadError}
            action={<Button size="small" onClick={load}>Retry</Button>} />
        ) : null}

        {loading && !detail ? (
          <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>
        ) : null}

        {wo ? (
          <>
            <WorkOrderStatsPanel
              detail={detail}
              canEditDelivery={mayRelease && wo.status !== 'CANCELLED'}
              onEditDelivery={() => openDlv(wo.deliveryDate)}
              onOpenProforma={wo.proformaId ? () => navigate(`/erp/sales/proformas/${wo.proformaId}`) : null}
            />

            {stageColumns.length ? (
              <>
                <div className="erp-production-section-title">
                  Production stages <Text type="secondary" style={{ fontWeight: 400, fontSize: 11.5 }}>(total completed pieces / routed per department)</Text>
                </div>
                <WorkOrderStageMatrix
                  stageColumns={stageColumns}
                  stageProgress={stageProgress}
                  total={orderedTotal}
                  done={completedPieces}
                />
              </>
            ) : null}

            <div className="erp-production-section-title">
              Lines &amp; pieces <Text type="secondary" style={{ fontWeight: 400, fontSize: 11.5 }}>({lines.length} line{lines.length === 1 ? '' : 's'} · {pieces.length} piece{pieces.length === 1 ? '' : 's'} — expand a line for its barcodes &amp; route)</Text>
            </div>
            <WorkOrderLineGrid lines={lines} pieces={pieces} fabLookups={fabLookups} lineRfd={detail && detail.lineRfd} />

            {/* Flat, searchable/exportable piece register for barcode lookup at scale.
                Collapsed by default + lazy-mounted so it never renders for browsing. */}
            <div className="mt-3">
              <Collapse
                items={[{
                  key: 'pieces',
                  label: <span style={{ fontWeight: 600 }}>All pieces — search &amp; export <Text type="secondary" style={{ fontWeight: 400, fontSize: 11.5 }}>({pieces.length} piece{pieces.length === 1 ? '' : 's'}; find a barcode / export CSV)</Text></span>,
                  children: <WorkOrderPiecesGrid pieces={pieces} lines={lines} woNo={wo.woNo} canPrint={mayPrint} onReprinted={load} />
                }]}
              />
            </div>

            <WorkOrderDguIssues workOrderId={id} />
          </>
        ) : null}

        {!mayView ? (
          <Alert type="warning" showIcon className="mt-3"
            message="You don't have permission to view work orders." />
        ) : null}

        <Modal open={dlvOpen} title="Expected delivery date" onCancel={() => setDlvOpen(false)}
          onOk={saveDlv} okText="Save" confirmLoading={dlvSaving} destroyOnClose>
          <p style={{ marginTop: 0, color: '#64748b' }}>The delivery commitment for this order — used by dispatch planning.</p>
          <DatePicker format="DD-MMM-YYYY" style={{ width: '100%' }} value={dlvDate}
            onChange={setDlvDate} placeholder="Select delivery date" />
        </Modal>
      </div>
    </ErpMasterShell>
  );
}
